import moment from 'moment';

const DDMMYYYY = 'DD/MM/YYYY';
const MAXPASSWORD = 12;
const MINPASSWORD = 6;
const YYYYMMDD = 'YYYY-MM-DD';

const exactLengthErrorMessage = 'support.components.inputs.exactLength';
const finalDateGreaterThanInitialDateErrorMessage = 'support.components.inputs.finalDateGreaterThanInitialDate';
const finalDateMaximumThanInitialDateErrorMessage = 'support.components.inputs.finalDateMaximumThanInitialDate';
const finalValueGreaterThanInitialValueErrorMessage = 'support.mixins.rules.finalValueGreaterThanInitialValue';
const initialDateLessThanFinalDateErrorMessage = 'support.components.inputs.initialDateLessThanFinalDate';
const initialDateMinimumThanFinalDateErrorMessage = 'support.components.inputs.initialDateMinimumThanFinalDate';
const initialValueLessThanFinalValueErrorMessage = 'support.mixins.rules.initialValueLessThanFinalValue';
const maxLengthErrorMessage = 'support.components.inputs.maxLength';
const maxPercentageLengthErrorMessage = 'support.mixins.rules.maxPercentageLength';
const minLengthErrorMessage = 'support.components.inputs.minLength';
const onlyAlphabeticCharactersErrorMessage = 'support.components.inputs.onlyAlphabeticCharacters';
const onlyAsciiCharactersAllowedErrorMessage = 'support.components.inputs.onlyAsciiCharactersAllowed';
const requiredErrorMessage = 'support.components.inputs.required';
const specialCharacterRequiredErrorMessagewLocal = 'support.components.inputs.specialCharacterRequired';
const mustBeLesser = 'support.components.inputs.mustBeLesserThan';
const hourMustBeLessarOrEqual = 'support.components.inputs.mustBeLesserOrEqualThan';
const hourMustBeBigger = 'support.components.inputs.mustBeBiggerThan';
const timeInvalid = 'support.components.inputs.invalidTime';
const onlyNumbersCommaSpace = 'support.components.inputs.onlyNumbersCommaSpace';

export default {
  computed: {
    isEmpty() {
      return (v) => {
        if (typeof v === 'boolean') {
          return true;
        }
        if (!v) return true;
        if (v.length === 0) {
          return this.$t(requiredErrorMessage);
        }
        return true;
      };
    },
    requiredRule() {
      return (v) => {
        if (typeof v === 'boolean') {
          return true;
        }
        if (typeof v === 'number' && v === 0) {
          return true;
        }
        if (typeof v === 'object') {
          if (Object.keys(v).length === 0) {
            return this.$t(requiredErrorMessage);
          }
        }
        return !!v || this.$t(requiredErrorMessage);
      };
    },
    strongPassword() {
      return (v) => {
        const regExStrongPassword = /^.*\W.*$/gm;
        return (
          regExStrongPassword.test(v)
          || this.$t(specialCharacterRequiredErrorMessagewLocal)
        );
      };
    },
    equalsToField() {
      return (v, comparisonField, errorMessage) => {
        if (!comparisonField || !v) return true;

        if (comparisonField !== v) {
          return this.$t(errorMessage);
        }
        return true;
      };
    },
    differsFromField() {
      return (v, comparisonField, errorMessage) => {
        if (!comparisonField || !v) return true;

        if (comparisonField === v) {
          return this.$t(errorMessage);
        }
        return true;
      };
    },
    passwordLength() {
      return (v) => {
        const minLength = !v
          || v.length >= MINPASSWORD
          || this.$t(minLengthErrorMessage, { length: MINPASSWORD });
        if (typeof minLength === 'string') return minLength;
        return (
          !v
          || v.length <= MAXPASSWORD
          || this.$t(maxLengthErrorMessage, { length: MAXPASSWORD })
        );
      };
    },
  },
  methods: {
    onlyNumbersCommaSpace() {
      return (v) => {
        if (!v || String(v).length === 0) return true;

        const regex = /^[-(\n),0-9]+$/gi;
        return regex.test(v) || this.$t(onlyNumbersCommaSpace);
      };
    },
    maxNumberLength(max) {
      return v => !v
        || v.toString().replace(/[^0-9]+/g, '').length <= max
        || this.$t(maxLengthErrorMessage);
    },
    maxLength(max) {
      return v => !v
        || v.length <= max
        || this.$t(maxLengthErrorMessage, { length: max });
    },
    minLength(min) {
      return v => !v
        || v.length >= min
        || this.$t(minLengthErrorMessage, { length: min });
    },
    exactLength(length) {
      return v => !v || v.length === length || this.$t(exactLengthErrorMessage);
    },
    exactNumberLength(length) {
      return v => !v
        || v.toString().replace(/[^0-9]+/g, '').length === length
        || this.$t(exactLengthErrorMessage);
    },
    maxPercentageLength(max) {
      return v => !v
        || Number(
          v
            .toString()
            .replace(/[^0-9]+/g, '')
            .replace(',', '.'),
        ) <= max
        || `${this.$t(maxPercentageLengthErrorMessage)} ${max}%`;
    },
    requiredRuleObject(test) {
      return (v) => {
        if (typeof v === 'undefined') {
          return this.$t(requiredErrorMessage);
        }
        if (typeof v[test] === 'boolean') {
          return true;
        }
        return !!v[test] || this.$t(requiredErrorMessage);
      };
    },
    requiredGiven(test) {
      return v => typeof v === 'boolean' || !!v || !test || this.$t(requiredErrorMessage);
    },
    dateGreaterThan(
      comparison,
      options = {
        valueDateFormat: DDMMYYYY,
        errorMessage: finalDateGreaterThanInitialDateErrorMessage,
      },
    ) {
      return (v) => {
        if (!comparison || !v) return true;
        const comparisonDate = moment(comparison, YYYYMMDD).format(YYYYMMDD);
        const dateTransformation = moment(
          v,
          options.valueDateFormat || DDMMYYYY,
        ).format(YYYYMMDD);
        if (moment(dateTransformation).isBefore(comparisonDate)) {
          return this.$t(
            options.errorMessage || finalDateGreaterThanInitialDateErrorMessage,
          );
        }
        return true;
      };
    },
    onlyAlphabeticCharacters() {
      const regex = /^[A-Za-zÀ-ÖØ-öø-ÿ ]+$/gi;
      return v => regex.test(v) || this.$t(onlyAlphabeticCharactersErrorMessage);
    },
    dateMaximumThan(comparison, maximum) {
      return (v) => {
        if (!comparison || !v) return true;
        const comparisonDate = moment(comparison, YYYYMMDD)
          .add(maximum, 'day')
          .format(YYYYMMDD);
        const dateTransformation = v
          .split('/')
          .reverse()
          .join('-');
        if (moment(dateTransformation).isAfter(comparisonDate)) {
          return this.$t(finalDateMaximumThanInitialDateErrorMessage, {
            days: maximum,
          });
        }
        return true;
      };
    },
    dateLesserThan(
      comparison,
      options = {
        valueDateFormat: DDMMYYYY,
        errorMessage: initialDateLessThanFinalDateErrorMessage,
      },
    ) {
      return (v) => {
        if (!comparison || !v) return true;
        const comparisonDate = moment(comparison, YYYYMMDD).format(YYYYMMDD);
        const dateTransformation = moment(
          v,
          options.valueDateFormat || DDMMYYYY,
        ).format(YYYYMMDD);
        if (moment(dateTransformation).isAfter(comparisonDate)) {
          return this.$t(
            options.errorMessage || initialDateLessThanFinalDateErrorMessage,
          );
        }
        return true;
      };
    },
    dateMinimumThan(comparison, minimum) {
      return (v) => {
        if (!comparison || !v) return true;
        const comparisonDate = moment(comparison, YYYYMMDD)
          .subtract(minimum, 'day')
          .format(YYYYMMDD);
        const dateTransformation = v
          .split('/')
          .reverse()
          .join('-');
        if (moment(dateTransformation).isBefore(comparisonDate)) {
          return this.$t(initialDateMinimumThanFinalDateErrorMessage, {
            days: minimum,
          });
        }
        return true;
      };
    },
    onlyAsciiCharacters() {
      return v => new RegExp('^[x00-x7F]*$').test(v)
        || this.$t(onlyAsciiCharactersAllowedErrorMessage);
    },
    valueLessThan(comparison) {
      return (value) => {
        if (!comparison || !value) return true;
        const floatValue = this.parseValueToFloat(`${value}`);
        if (floatValue > comparison) {
          return this.$t(initialValueLessThanFinalValueErrorMessage);
        }
        return true;
      };
    },
    valueGreaterThan(comparison) {
      return (value) => {
        if (!comparison || !value) return true;
        const floatValue = this.parseValueToFloat(`${value}`);
        if (floatValue < this.parseValueToFloat(`${comparison}`)) {
          return this.$t(finalValueGreaterThanInitialValueErrorMessage);
        }
        return true;
      };
    },
    parseValueToFloat(value) {
      return parseFloat(
        value
          .replace(/[.R$]/gi, '')
          .replace(',', '.')
          .trim(),
      );
    },
    maxValue(comparison) {
      return (value) => {
        if (!comparison || !value) return true;
        const floatValue = this.parseValueToFloat(`${value}`);
        if (floatValue > comparison) {
          return this.$t(mustBeLesser, {
            num: comparison,
          });
        }
        return true;
      };
    },
    minTimeValue(comparison) {
      return (value) => {
        const timeValue = moment(value, 'HH:mm');
        const minTimeValue = moment(comparison, 'HH:mm');
        if (minTimeValue.isAfter(timeValue)) {
          return this.$t(hourMustBeBigger, {
            num: comparison,
          });
        }
        return true;
      };
    },
    maxTimeValue(comparison, lesserOrEqual = true) {
      return (value) => {
        const timeValue = moment(value, 'HH:mm');
        const maxTimeValue = moment(comparison, 'HH:mm');
        if (maxTimeValue.isBefore(timeValue)) {
          const message = lesserOrEqual
            ? hourMustBeLessarOrEqual
            : mustBeLesser;
          return this.$t(message, {
            num: comparison,
          });
        }
        return true;
      };
    },
    isValidValue() {
      return (value) => {
        if (!value) return true;
        const timeValue = moment(value, 'HH:mm');
        if (!timeValue.isValid()) {
          return this.$t(timeInvalid);
        }
        return true;
      };
    },
  },
};
